export type MicrophoneAccessFailureReason =
  | "unsupported"
  | "denied"
  | "not_found"
  | "unavailable"
  | "error";

export type MicrophoneAccessResult =
  | { ok: true }
  | { ok: false; reason: MicrophoneAccessFailureReason };

export const getMicrophoneAccessErrorMessage = (
  reason: MicrophoneAccessFailureReason,
): string => {
  switch (reason) {
    case "unsupported":
      return "Your browser does not allow microphone access on this page.";
    case "denied":
      return "Enable microphone access in your browser settings to place this call.";
    case "not_found":
      return "No microphone was detected. Turn it on or plug one in, then try again.";
    case "unavailable":
      return "The microphone is off or in use by another app. Turn it on and try again.";
    default:
      return "Could not access the microphone. Check that it is connected and turned on.";
  }
};

const reasonFromMediaError = (error: unknown): MicrophoneAccessFailureReason => {
  const name =
    error instanceof DOMException
      ? error.name
      : typeof error === "object" && error && "name" in error
        ? String((error as { name?: string }).name)
        : "";

  if (name === "NotAllowedError" || name === "PermissionDeniedError") return "denied";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "not_found";
  if (name === "NotReadableError" || name === "AbortError" || name === "OverconstrainedError") {
    return "unavailable";
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String((error as { message?: string }).message)
      : String(error ?? "");

  if (/permission denied|not allowed/i.test(message)) return "denied";
  if (/requested device not found|not found/i.test(message)) return "not_found";
  if (/could not start audio|not readable|device in use/i.test(message)) return "unavailable";

  return "error";
};

/**
 * Opens the mic while a user gesture is still active (call / accept click).
 * Always uses getUserMedia so a hardware mute or unplugged device is not treated as OK
 * just because browser permission was already granted.
 * Stops tracks immediately — Twilio / LiveKit acquire their own stream next.
 */
export const ensureMicrophonePermission = async (): Promise<MicrophoneAccessResult> => {
  if (typeof window === "undefined" || !window.isSecureContext) {
    return { ok: false, reason: "unsupported" };
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: "unsupported" };
  }

  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      if (status.state === "denied") {
        return { ok: false, reason: "denied" };
      }
    }
  } catch {
    // Fall through to getUserMedia when Permissions API is unavailable.
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: reasonFromMediaError(error) };
  }
};

export const getCallStartMediaErrorMessage = (error: unknown): string => {
  return getMicrophoneAccessErrorMessage(reasonFromMediaError(error));
};
